/*
 * Promo popup — trigger and frequency rules only. Opening, closing, focus
 * trapping, Escape, click-outside, scroll lock, inert background and
 * one-overlay-at-a-time all come from the shared OverlayPanel
 * (assets/overlay-panel.js), the same module behind the header navigation and
 * the cart and search drawers.
 *
 * Because this modal opens without being asked, it holds back rather than
 * interrupting:
 *   · it never opens while another overlay is already open
 *   · it stops its timer and scroll listener once shown or dismissed
 *   · focus lands on the close button — the way out is the first thing you meet
 *   · dismissal is remembered for the configured window
 *
 * A signup counts as a dismissal wherever it was made — the popup's own form,
 * the footer's, an Email signup block's (assets/newsletter-form.js answers in
 * place and announces it): someone who has just subscribed isn't asked again.
 *
 * Storage degrades quietly: if localStorage is unavailable (private mode, an
 * embedded webview, storage disabled) the popup still works, it just can't
 * remember the dismissal.
 */

// Bare names, resolved by the import map in layout/theme.liquid to the
// versioned asset URLs (a relative './' path would bypass cache-busting).
import { OverlayPanel } from 'overlay-panel';
// The popup's signup form sends through it, even on a page with no other signup.
import 'newsletter-form';

/* Storage can throw on access, not just on write — guard both. */
const readStore = (session) => {
  try {
    return session ? window.sessionStorage : window.localStorage;
  } catch (error) {
    return null;
  }
};

const init = (root) => {
  if (!root || root.dataset.popupReady) return;
  root.dataset.popupReady = '1';

  /* Visual preview renders the section for its preset thumbnail. Liquid has
     already laid the popup open and in flow — no triggers, no scroll lock, no
     focus stealing, or the thumbnail would capture a locked page. */
  if (window.Shopify && window.Shopify.visualPreviewMode) return;

  const dialog = root.querySelector('[data-popup-dialog]');
  const closeButton = root.querySelector('.promo-popup__close');
  const { trigger, frequency, storageKey } = root.dataset;
  const delayMs = (parseFloat(root.dataset.delay) || 0) * 1000;
  const scrollTarget = parseFloat(root.dataset.scroll) || 0;
  const enabled = root.dataset.enabled === 'true';
  const showOn = root.dataset.showOn || 'all';
  const template = root.dataset.template || '';
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  let timer = null;
  let shown = false;

  const store = readStore(frequency === 'session');

  const isDismissed = () => {
    if (frequency === 'always' || !store) return false;
    try {
      const raw = store.getItem(storageKey);
      if (!raw) return false;
      if (frequency === 'session') return true; // cleared when the tab closes
      const at = parseInt(raw, 10);
      if (!Number.isFinite(at)) return false;
      const days = parseInt(frequency, 10);
      if (!Number.isFinite(days)) return false;
      return Date.now() - at < days * 24 * 60 * 60 * 1000;
    } catch (error) {
      return false;
    }
  };

  const remember = () => {
    if (frequency === 'always' || !store) return;
    try {
      store.setItem(storageKey, String(Date.now()));
    } catch (error) {
      /* Quota or a blocked store — the popup simply shows again next time. */
    }
  };

  const panel = new OverlayPanel({
    root,
    dialog,
    // No trigger element exists, so hand focus back to the main landmark.
    returnFocusTo: document.getElementById('main-content'),
    onOpen: () => {
      // Meet the visitor with the way out, not the marketing.
      if (closeButton) closeButton.focus();
    },
    onClose: () => remember(),
  });

  const teardown = () => {
    window.clearTimeout(timer);
    window.removeEventListener('scroll', onScroll);
  };

  /* Is the visitor in the middle of something? One of the theme's overlays, a
     native dialog (the image viewer - opening under it would make the page
     behind it inert, viewer included) or a field they are typing in: taking
     focus away from any of those is a change of context nobody asked for
     (WCAG 3.2.1). */
  const visitorIsBusy = () => {
    const active = document.activeElement;
    return (
      document.documentElement.classList.contains('overlay-is-open') ||
      Boolean(document.querySelector('dialog[open]')) ||
      Boolean(active && active.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable]:not([contenteditable="false"])'))
    );
  };

  const show = () => {
    if (shown || panel.isOpen) return;
    // Never talk over the visitor: look again in a few seconds instead.
    if (visitorIsBusy()) {
      window.clearTimeout(timer);
      timer = window.setTimeout(show, 4000);
      return;
    }
    shown = true;
    teardown();
    panel.open();
  };

  function onScroll() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const percent = (window.scrollY / scrollable) * 100;
    if (percent >= scrollTarget) show();
  }

  // Wired before any early return, so the close control works in the editor too.
  root.querySelectorAll('[data-popup-close]').forEach((closer) => {
    closer.addEventListener('click', () => panel.close());
  });

  /* The theme editor previews on demand: no timers, no frequency cap, so the
     merchant can see the popup while editing it. */
  if (designMode) {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.contains(root)) panel.open();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target.contains(root)) panel.close();
    });
    return;
  }

  /* A signup anywhere on the page counts as the dismissal: remembered, and a
     timer or scroll watch still waiting stops. Made in the popup itself, the
     popup stays open on its thank-you message until they close it. */
  document.addEventListener('newsletter:subscribed', () => {
    remember();
    shown = true;
    teardown();
  });

  if (!enabled) return;
  if (showOn === 'index' && template !== 'index') return;
  if (isDismissed()) return;

  if (trigger === 'delay' || trigger === 'both') {
    timer = window.setTimeout(show, delayMs);
  }
  if (trigger === 'scroll' || trigger === 'both') {
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // a short page may already be past the threshold
  }
};

const initAll = () => document.querySelectorAll('[data-promo-popup]').forEach(init);

if (document.readyState !== 'loading') initAll();
else document.addEventListener('DOMContentLoaded', initAll);

// Theme editor: re-wire a freshly reloaded section.
document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector('[data-promo-popup]');
  if (root) init(root);
});
